import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Package, Loader2, Download, Check, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  Draft: "bg-gray-100 text-gray-600 border-gray-200",
  Picking: "bg-amber-50 text-amber-700 border-amber-200",
  Packed: "bg-blue-50 text-blue-700 border-blue-200",
  Shipped: "bg-green-50 text-green-700 border-green-200",
};

export default function PackingSlipsPage() {
  const { id } = useParams();
  if (id === "new") return <CreatePackingSlip />;
  if (id) return <PackingSlipDetail psId={id} />;
  return <PackingSlipList />;
}

function PackingSlipList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/packing-slips?status=${filter}&search=${search}&limit=50`)
      .then(r => { setItems(r.data.packing_slips); setTotal(r.data.total); setStats(r.data.stats || {}); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [filter, search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="packing-slips-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Packing Slips</h1>
          <p className="text-sm text-gray-500 mt-1">{total} packing slip{total !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="create-packing-slip-btn" onClick={() => navigate("/packing-slips/new")} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" />New Packing Slip
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[["Draft", "draft", "text-gray-600 bg-gray-50"], ["Picking", "picking", "text-amber-600 bg-amber-50"], ["Packed", "packed", "text-blue-600 bg-blue-50"], ["Shipped", "shipped", "text-green-600 bg-green-50"]].map(([label, key, cls]) => (
          <div key={key} className={`p-3 rounded-sm border ${cls}`}>
            <div className="text-lg font-bold">{stats[key] || 0}</div>
            <div className="text-xs font-medium">{label}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-4">
        <Input data-testid="ps-search-input" placeholder="Search packing slips..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs rounded-sm" />
        {["", "Draft", "Picking", "Packed", "Shipped"].map(s => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-sm text-xs ${filter === s ? "bg-[#E63946] text-white" : ""}`}>{s || "All"}</Button>
        ))}
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Package className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No packing slips found</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="packing-slips-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Number</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Customer</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Source</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Items</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Packed By</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
            </tr></thead>
            <tbody>{items.map(ps => (
              <tr key={ps.ps_id} data-testid={`ps-row-${ps.ps_id}`} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/packing-slips/${ps.ps_id}`)}>
                <td className="p-3 font-medium text-[#E63946]">{ps.packing_slip_number}</td>
                <td className="p-3 text-gray-600">{formatDate(ps.created_at)}</td>
                <td className="p-3 text-gray-700">{ps.customer_name}</td>
                <td className="p-3"><Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{ps.source_type} {ps.source_number}</Badge></td>
                <td className="p-3 text-gray-600">{(ps.line_items || []).length}</td>
                <td className="p-3 text-gray-600">{ps.packed_by || "-"}</td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${statusColors[ps.status] || "bg-gray-100"}`}>{ps.status}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      )}
    </div>
  );
}

function CreatePackingSlip() {
  const navigate = useNavigate();
  const [sourceType, setSourceType] = useState("invoice");
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const endpoint = sourceType === "invoice" ? "/invoices?limit=200" : "/quotes?limit=200";
    api.get(endpoint).then(r => {
      setSources(r.data.invoices || r.data.quotes || []);
    }).catch(() => setSources([]));
    setSelectedSource("");
  }, [sourceType]);

  const getSourceId = (src) => src.invoice_id || src.quote_id;
  const getSourceNum = (src) => src.invoice_number || src.quote_number;

  const save = async () => {
    if (!selectedSource) return toast.error("Please select a source document");
    setSaving(true);
    try {
      const res = await api.post("/packing-slips", { source_type: sourceType, source_id: selectedSource, notes });
      toast.success("Packing slip created");
      navigate(`/packing-slips/${res.data.ps_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="create-packing-slip">
      <Button variant="ghost" size="sm" onClick={() => navigate("/packing-slips")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
      <h1 className="font-heading font-bold text-2xl text-[#1F2937] mb-6">New Packing Slip</h1>
      <Card className="border rounded-sm"><CardContent className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Source Type</label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger data-testid="ps-source-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="invoice">Invoice</SelectItem>
                <SelectItem value="quote">Quote</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Source Document</label>
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger data-testid="ps-source-doc-select" className="rounded-sm"><SelectValue placeholder="Select document..." /></SelectTrigger>
              <SelectContent>{sources.map(s => (
                <SelectItem key={getSourceId(s)} value={getSourceId(s)}>{getSourceNum(s)} - {s.customer_name}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notes</label>
          <textarea data-testid="ps-notes-input" className="w-full rounded-sm border p-2.5 text-sm" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="flex gap-2 pt-2">
          <Button data-testid="save-packing-slip-btn" onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Create Packing Slip
          </Button>
          <Button variant="outline" onClick={() => navigate("/packing-slips")} className="rounded-sm">Cancel</Button>
        </div>
      </CardContent></Card>
    </div>
  );
}

function PackingSlipDetail({ psId }) {
  const navigate = useNavigate();
  const [ps, setPs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [actioning, setActioning] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [editItems, setEditItems] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/packing-slips/${psId}`)
      .then(r => { setPs(r.data); setEditItems((r.data.line_items || []).map(i => ({...i}))); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [psId]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async () => {
    setActioning(true);
    try {
      await api.post(`/packing-slips/${psId}/update-status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`); setStatusDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const saveItems = async () => {
    setActioning(true);
    try {
      await api.put(`/packing-slips/${psId}`, { line_items: editItems });
      toast.success("Items updated"); setEditingItems(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const downloadPdf = () => {
    api.get(`/packing-slips/${psId}/pdf`, { responseType: "blob" }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `packing-slip-${ps?.packing_slip_number || ""}.pdf`; a.click();
    }).catch(() => toast.error("Failed to download PDF"));
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!ps) return <div className="p-8 text-center text-gray-400">Not found</div>;

  const validTransitions = { Draft: ["Picking"], Picking: ["Packed", "Draft"], Packed: ["Shipped", "Picking"] };
  const allowed = validTransitions[ps.status] || [];

  return (
    <div data-testid="packing-slip-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/packing-slips")}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{ps.packing_slip_number}</h1>
          <p className="text-sm text-gray-500">From {ps.source_type} {ps.source_number} | {ps.customer_name}</p>
        </div>
        <Badge className={`rounded-full text-sm px-3 py-1 border ${statusColors[ps.status]}`}>{ps.status}</Badge>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {allowed.length > 0 && <Button data-testid="ps-change-status-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setStatusDialog(true)}><ClipboardList className="w-4 h-4 mr-1" />Change Status</Button>}
        {ps.status !== "Shipped" && <Button data-testid="ps-edit-items-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setEditingItems(true)}><Package className="w-4 h-4 mr-1" />Edit Picks</Button>}
        <Button data-testid="download-ps-pdf-btn" size="sm" variant="outline" className="rounded-sm" onClick={downloadPdf}><Download className="w-4 h-4 mr-1" />PDF</Button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Source</div>
          <div className="text-sm"><span className="font-medium">{ps.source_type}</span> {ps.source_number}</div>
        </CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Created</div>
          <div className="text-sm">{formatDate(ps.created_at)} by {ps.created_by_name}</div>
        </CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Packed</div>
          <div className="text-sm">{ps.packed_by ? `${ps.packed_by} on ${formatDate(ps.packed_at)}` : "Not yet packed"}</div>
        </CardContent></Card>
      </div>

      {/* Line Items */}
      <Card className="border rounded-sm mb-6"><div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Line Items</h3>
        {editingItems ? (
          <>
            <table data-testid="ps-edit-items-table" className="w-full text-sm mb-3">
              <thead><tr className="border-b bg-gray-50/50">
                <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">SKU</th>
                <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">Description</th>
                <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">Bin Location</th>
                <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Qty</th>
                <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Picked</th>
              </tr></thead>
              <tbody>{editItems.map((item, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="p-2.5 font-mono text-xs">{item.sku || "-"}</td>
                  <td className="p-2.5">{item.description}</td>
                  <td className="p-2.5"><Input className="h-8 w-28 text-xs rounded-sm" value={item.bin_location || ""} onChange={(e) => { const u = [...editItems]; u[i].bin_location = e.target.value; setEditItems(u); }} /></td>
                  <td className="p-2.5 text-right">{item.quantity}</td>
                  <td className="p-2.5 text-right"><Input type="number" className="h-8 w-20 text-xs text-right rounded-sm" value={item.picked_qty} onChange={(e) => { const u = [...editItems]; u[i].picked_qty = parseFloat(e.target.value) || 0; setEditItems(u); }} /></td>
                </tr>
              ))}</tbody>
            </table>
            <div className="flex gap-2">
              <Button data-testid="save-picks-btn" size="sm" onClick={saveItems} disabled={actioning} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Save</Button>
              <Button size="sm" variant="outline" onClick={() => { setEditingItems(false); setEditItems((ps.line_items || []).map(i => ({...i}))); }} className="rounded-sm">Cancel</Button>
            </div>
          </>
        ) : (
          <table data-testid="ps-items-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">SKU</th>
              <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">Description</th>
              <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">Bin Location</th>
              <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Qty</th>
              <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Picked</th>
              <th className="p-2.5 text-center text-xs font-semibold uppercase text-gray-500">Status</th>
            </tr></thead>
            <tbody>{(ps.line_items || []).map((item, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="p-2.5 font-mono text-xs">{item.sku || "-"}</td>
                <td className="p-2.5">{item.description}</td>
                <td className="p-2.5 text-gray-500">{item.bin_location || "-"}</td>
                <td className="p-2.5 text-right">{item.quantity}</td>
                <td className="p-2.5 text-right font-medium">{item.picked_qty || 0}</td>
                <td className="p-2.5 text-center">{item.picked_qty >= item.quantity ? <Check className="w-4 h-4 text-green-500 mx-auto" /> : <span className="text-amber-500 text-xs">Pending</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div></Card>

      {ps.notes && <Card className="border rounded-sm mb-6"><CardContent className="p-4"><div className="text-xs font-semibold text-gray-500 uppercase mb-1">Notes</div><p className="text-sm text-gray-700">{ps.notes}</p></CardContent></Card>}

      {/* Audit Trail */}
      {ps.audit_trail?.length > 0 && (
        <Card className="border rounded-sm"><CardContent className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Activity Log</h3>
          <div className="space-y-2">{ps.audit_trail.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-xs text-gray-500 border-b border-gray-50 pb-2">
              <span className="text-gray-400 w-32">{formatDate(a.created_at)}</span>
              <span className="font-medium text-gray-700">{a.user_name}</span>
              <span>{a.details}</span>
            </div>
          ))}</div>
        </CardContent></Card>
      )}

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent><DialogHeader><DialogTitle>Change Status</DialogTitle></DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger data-testid="ps-new-status-select"><SelectValue placeholder="Select new status..." /></SelectTrigger>
            <SelectContent>{allowed.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>Cancel</Button>
            <Button data-testid="ps-confirm-status-btn" onClick={changeStatus} disabled={actioning || !newStatus} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
